/**
 * Serviço de Conectividade
 * Monitora conexão de rede em tempo real
 */
import { ConnectivityRestoredEvent, ConnectivityLostEvent } from '../events/lifecycle-events';

export class ConnectivityService {
  private static isConnected = navigator.onLine;
  private static listeners: ((isConnected: boolean) => void)[] = [];
  private static eventListeners: ((event: ConnectivityRestoredEvent | ConnectivityLostEvent) => void)[] = [];
  private static isInitialized = false;

  static initialize(): void {
    if (this.isInitialized) return;

    window.addEventListener('online', this.handleOnline.bind(this));
    window.addEventListener('offline', this.handleOffline.bind(this));

    this.isInitialized = true;
  }

  private static handleOnline(): void {
    const wasOffline = !this.isConnected;
    this.isConnected = true;
    
    this.notifyListeners(true);
    
    if (wasOffline) {
      const event = new ConnectivityRestoredEvent();
      this.notifyEventListeners(event);
    }
  }

  private static handleOffline(): void {
    const wasOnline = this.isConnected;
    this.isConnected = false;
    
    this.notifyListeners(false);
    
    if (wasOnline) {
      const event = new ConnectivityLostEvent();
      this.notifyEventListeners(event);
    }
  }

  private static notifyListeners(isConnected: boolean): void {
    this.listeners.forEach(listener => {
      try {
        listener(isConnected);
      } catch (error) {
        console.error('Error in connectivity listener:', error);
      }
    });
  }

  private static notifyEventListeners(event: ConnectivityRestoredEvent | ConnectivityLostEvent): void {
    this.eventListeners.forEach(listener => {
      try {
        listener(event);
      } catch (error) {
        console.error('Error in connectivity event listener:', error);
      }
    });
  }

  static addListener(listener: (isConnected: boolean) => void): void {
    this.listeners.push(listener);
  }

  static removeListener(listener: (isConnected: boolean) => void): void {
    const index = this.listeners.indexOf(listener);
    if (index > -1) {
      this.listeners.splice(index, 1);
    }
  }

  static addEventListener(listener: (event: ConnectivityRestoredEvent | ConnectivityLostEvent) => void): void {
    this.eventListeners.push(listener);
  }

  static removeEventListener(listener: (event: ConnectivityRestoredEvent | ConnectivityLostEvent) => void): void {
    const index = this.eventListeners.indexOf(listener);
    if (index > -1) {
      this.eventListeners.splice(index, 1);
    }
  }

  static get hasConnectivity(): boolean {
    return this.isConnected;
  }

  static async checkCurrentConnectivity(): Promise<boolean> {
    // Para uma verificação mais robusta, você pode fazer uma requisição real
    try {
      const response = await fetch('/favicon.ico', { 
        method: 'HEAD',
        cache: 'no-cache',
        mode: 'no-cors'
      });
      return true;
    } catch {
      return false;
    }
  }

  static dispose(): void {
    window.removeEventListener('online', this.handleOnline.bind(this));
    window.removeEventListener('offline', this.handleOffline.bind(this));
    this.listeners.length = 0;
    this.eventListeners.length = 0;
    this.isInitialized = false;
  }
}
